/**
 * hpq-free-ship.js — single source of truth for free-shipping state.
 *
 * Before this module, three bars (cart-page bar in hpq-cart.js, drawer bar in
 * hpq-quick-cart.js, nav announcement in hpq-announcement.js) each carried an
 * identical copy of effectiveTotalCents() + THRESHOLD and each subscribed to the
 * cart/quote events on its own. A change had to be made in three places and they
 * could drift. This module owns the threshold, the effective (coupon-adjusted)
 * order total, and the derived status; every bar reads stateFor()/getState() and
 * re-renders on the single 'hpq:freeship-changed' event.
 *
 * EFFECTIVE TOTAL: in draft-checkout mode a cart coupon lowers the ORDER total
 * below cart.total_price (the code is cart-only, never pushed into the Shopify
 * cart), and Shopify evaluates the $59 free-ship rate on that lower order total
 * at checkout. The bars MUST key on the worker's quoted order total
 * (window.hpqCouponQuote) so none of them ever promises free shipping the
 * checkout won't honour. No coupon → quote is null → effective == cart total.
 *
 * PENDING: a coupon quote is a ~1s worker round-trip. While it is in flight the
 * published quote is stale, so status becomes 'pending' and the bars show a
 * neutral "Checking shipping…" instead of flashing the pre-coupon claim (e.g.
 * "FREE SHIPPING") and then snapping to "$9". hpq-cart-coupon.js dispatches
 * 'hpq:quote-pending' when it starts a network quote; 'hpq:quote-updated' (fired
 * when the quote lands or clears) drops pending. A 6s watchdog guarantees a lost
 * quote can never strand the bars on "Checking…".
 */
(function () {
  'use strict';

  var THRESHOLD = (window.HPQ_CONFIG && window.HPQ_CONFIG.freeShippingThreshold) || 5900;

  var state = {
    cart: null,
    total: null,       // latest local cart total_price
    itemCount: null,   // latest local item_count
    currency: 'USD'
  };
  var pending = false;     // coupon quote is in flight
  var pendingTimer = null;

  function toNumber(v) {
    return (typeof v === 'number' && isFinite(v)) ? Math.max(0, v | 0) : 0;
  }
  function normalizeCode(s) {
    return String(s || '').toUpperCase();
  }
  function enabled() {
    return (window.HPQ_CONFIG && window.HPQ_CONFIG.draftCheckoutEnabled === true) || window.HPQ_FORCE_DRAFT_CHECKOUT === true;
  }
  function getAppliedCoupons() {
    try {
      return (window.hpqDraftCheckout && typeof window.hpqDraftCheckout.getCodes === 'function')
        ? window.hpqDraftCheckout.getCodes()
        : [];
    } catch (e) {
      return [];
    }
  }
  function getQuote() {
    return window.hpqCouponQuote || null;
  }
  function isAcceptedCode(code, quote) {
    var list = (quote && Array.isArray(quote.acceptedCodes)) ? quote.acceptedCodes : [];
    var normCode = normalizeCode(code);
    for (var i = 0; i < list.length; i++) {
      if (normalizeCode(list[i]) === normCode) return true;
    }
    return false;
  }

  function readCurrency(cart) {
    var c = cart && cart.currency;
    if (typeof c === 'string' && c) return c;
    if (c && typeof c === 'object') return String(c.iso_code || c.code || c.currency || c.currency_code || c || 'USD');
    return 'USD';
  }

  // Pure derivation used by getVM/stateFor + T1 tests.
  function computeTotals(cart, quote, coupons, isEnabled) {
    var c = cart || {};
    var isEnabledFlag = !!isEnabled;
    var itemCount = (typeof c.item_count === 'number') ? c.item_count : null;
    var subtotalCents = toNumber(c.original_total_price);
    var automaticSavingsCents = toNumber(c.total_discount);
    var cartTotalCents = toNumber(c.total_price);

    var codeDiscountCents = 0;
    var promoCode = null;
    var hasAcceptedCode = false;
    var normalizedCoupons = [];
    for (var i = 0; i < (coupons || []).length; i++) {
      normalizedCoupons.push(normalizeCode(coupons[i]));
    }

    if (isEnabledFlag && normalizedCoupons.length) {
      var accepted = (quote && Array.isArray(quote.acceptedCodes)) ? quote.acceptedCodes : [];
      for (var j = 0; j < normalizedCoupons.length; j++) {
        if (isAcceptedCode(normalizedCoupons[j], quote)) {
          hasAcceptedCode = true;
          promoCode = normalizedCoupons[j];
          break;
        }
      }
      if (hasAcceptedCode) codeDiscountCents = (quote && typeof quote.codeDiscountCents === 'number') ? quote.codeDiscountCents : 0;
    }

    var totalCents = hasAcceptedCode ? ((quote && typeof quote.orderTotalCents === 'number') ? quote.orderTotalCents : cartTotalCents) : cartTotalCents;
    var remainingCents = Math.max(0, THRESHOLD - totalCents);
    var pct = Math.max(0, Math.min(100, Math.round((totalCents / THRESHOLD) * 100)));
    var status = (itemCount === 0) ? 'empty' : (totalCents >= THRESHOLD ? 'free' : 'away');

    return {
      enabled: isEnabledFlag,
      currency: readCurrency(c),
      subtotalCents: subtotalCents,
      automaticSavingsCents: automaticSavingsCents,
      promoCode: promoCode,
      promoDiscountCents: codeDiscountCents,
      totalCents: totalCents,
      freeShip: {
        threshold: THRESHOLD,
        status: status,
        remainingCents: remainingCents,
        pct: pct,
        pending: false
      }
    };
  }

  function freeShipLegacy(state) {
    var legacy = {
      threshold: state.freeShip.threshold,
      effectiveCents: state.totalCents,
      cartTotalCents: state.rawCartTotalCents,
      itemCount: state.rawItemCount,
      remainingCents: state.freeShip.remainingCents,
      pct: state.freeShip.pct,
      pending: state.freeShip.pending,
      status: state.freeShip.status === 'away' ? 'flat' : state.freeShip.status
    };
    if (typeof state.freeShip.status === 'string' && !state.freeShip.pending && state.freeShip.status === 'pending') {
      legacy.status = 'pending';
    }
    return legacy;
  }

  function getVM() {
    var isEnabledFlag = enabled();
    var c = state.cart || {};
    var quote = getQuote();
    var coupons = getAppliedCoupons();
    var vm = computeTotals(c, quote, coupons, isEnabledFlag);
    vm.freeShip.pending = pending;
    if (vm.freeShip.pending) vm.freeShip.status = 'pending';
    vm.freeShip.effectiveCents = vm.totalCents;
    vm.currency = state.currency || vm.currency;
    vm.rawCartTotalCents = typeof state.total === 'number' ? state.total : null;
    vm.rawItemCount = typeof state.itemCount === 'number' ? state.itemCount : null;
    return vm;
  }

  function getState() {
    var s = getVM();
    return {
      threshold: s.freeShip.threshold,
      effectiveCents: s.freeShip.effectiveCents,
      cartTotalCents: s.rawCartTotalCents,
      itemCount: s.rawItemCount,
      remainingCents: s.freeShip.remainingCents,
      pct: s.freeShip.pct,
      pending: s.freeShip.pending,
      status: s.freeShip.status === 'away' ? 'flat' : s.freeShip.status
    };
  }

  function stateFor(total, count) {
    var next = state.cart || {};
    var hasTotal = (typeof total === 'number');
    if (hasTotal) {
      next = {
        total_price: total,
        item_count: (typeof count === 'number') ? count : null,
        original_total_price: (typeof total === 'number') ? total : 0,
        total_discount: 0,
        currency: state.cart && state.cart.currency || 'USD'
      };
    }
    var isEnabledFlag = enabled();
    var coupons = getAppliedCoupons();
    var vm = computeTotals(next, getQuote(), coupons, isEnabledFlag);
    vm.freeShip.pending = pending;
    if (vm.freeShip.pending) vm.freeShip.status = 'pending';
    vm.freeShip.effectiveCents = vm.totalCents;
    return {
      threshold: vm.freeShip.threshold,
      effectiveCents: vm.totalCents,
      cartTotalCents: hasTotal ? toNumber(total) : (typeof state.total === 'number' ? state.total : null),
      itemCount: (typeof count === 'number') ? count : (typeof state.itemCount === 'number' ? state.itemCount : null),
      remainingCents: vm.freeShip.remainingCents,
      pct: vm.freeShip.pct,
      pending: pending,
      status: vm.freeShip.status === 'away' ? 'flat' : vm.freeShip.status
    };
  }

  function emit() {
    var vm = getVM();
    var legacy = freeShipLegacy(vm);
    document.dispatchEvent(new CustomEvent('hpq:cart-changed', { detail: vm }));
    document.dispatchEvent(new CustomEvent('hpq:freeship-changed', { detail: legacy }));
  }

  function setCart(cart) {
    if (!cart) return;
    state.cart = cart;
    state.total = toNumber(cart.total_price);
    state.itemCount = (typeof cart.item_count === 'number') ? cart.item_count : state.itemCount;
    state.currency = readCurrency(cart);
  }

  function clearPending() {
    if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }
    if (pending) {
      pending = false;
      emit();
    }
  }
  // Arm the flag + watchdog WITHOUT emitting, for callers that are about to emit anyway.
  // Splitting this out keeps a cart mutation to one emit instead of two.
  function armPending() {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(clearPending, 6000); // watchdog: never strand on "Checking…"
    pending = true;
  }
  function setPending() {
    var was = pending;
    armPending();
    if (!was) emit();
  }

  // STALE QUOTE: a published coupon quote was priced for the cart as it was BEFORE this change.
  // Pairing it with the new cart is what paints Total > Subtotal for a frame, or a free-ship
  // claim the coupon has already invalidated. The ordering is structural, not incidental:
  // hpq-free-ship.js loads before hpq-cart-coupon.js, so this module's cart:updated listener
  // always runs FIRST and would emit that stale pairing before the coupon module can re-quote.
  //
  // Hold the neutral "Checking…" state instead. hpq:quote-updated clears it, and the coupon
  // module publishes on its cached path too, so an unchanged cart signature clears immediately
  // with no network wait. The 6s watchdog guarantees we never strand on "Checking…".
  //
  // Only for carts that actually have a coupon applied — everyone else keeps the instant
  // granular number, which is the whole point of the optimistic local cart.
  function emitAfterCartChange() {
    if (getAppliedCoupons().length) armPending();
    emit();
  }

  // STALE RESPONSES: /cart.js describes the cart as it was when the request was ISSUED. The
  // local cart is write-behind (hpq-local-cart.js debounces the mutation ~80ms and the POST
  // commits later), so a GET that overlaps a commit comes back describing the cart BEFORE the
  // shopper's latest clicks. Applying it rolls the effective total backwards, and because every
  // bar reads this one store they ALL flash "Add $X more for FREE shipping" for one round-trip
  // right after the shopper crossed the threshold. Two ways a body can be behind the screen:
  //   1. a newer fetch has since been issued (responses can also land out of order), or
  //   2. the local cart still holds edits Shopify has not acknowledged (isDirty()).
  // Either way the body is older than what the bars already show, so drop it. Nothing is lost:
  // the sync that carries those edits ends in 'hpq:cart-synced', which fetches again.
  var fetchGeneration = 0;

  function hasUnsyncedLocalEdits() {
    try {
      return !!(window.hpqCart && typeof window.hpqCart.isDirty === 'function' && window.hpqCart.isDirty());
    } catch (e) {
      return false; // no local cart module → /cart.js is the only source, always apply
    }
  }

  function fetchCart() {
    var generation = ++fetchGeneration;
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (generation !== fetchGeneration || hasUnsyncedLocalEdits()) return cart;
        setCart(cart);
        emitAfterCartChange();
        return cart;
      })
      .catch(function () { return null; });
  }

  window.__hpqTestComputeTotals = computeTotals;

  // ── Inputs ─────────────────────────────────────────────────────────────
  document.addEventListener('cart:updated', function (e) {
    var cart = (e && e.detail && e.detail.cart) || (e && e.detail);
    if (cart && typeof cart.total_price === 'number') {
      setCart(cart);
      emitAfterCartChange();
    } else {
      fetchCart();
    }
  });
  document.addEventListener('cart:item-added', function () { setTimeout(fetchCart, 300); });
  document.addEventListener('shopify:section:load', fetchCart);
  document.addEventListener('hpq:cart-synced', fetchCart);
  // Coupon quote landed (or cleared) → refresh effective total + drop pending.
  document.addEventListener('hpq:quote-updated', function () { clearPending(); emit(); });
  // Coupon quote started → neutral "Checking…" until quote lands or times out.
  document.addEventListener('hpq:quote-pending', setPending);

  window.hpqCartState = {
    getVM: getVM,
    getState: getState,
    refresh: fetchCart
  };

  window.hpqFreeShip = {
    threshold: THRESHOLD,
    stateFor: stateFor,
    getState: getState,
    refresh: fetchCart
  };

  // Prime the tracked cart once so getState() is meaningful before first event.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fetchCart);
  else fetchCart();
})();
